<div
  class="input-container"
  [attr.data-size]="size"
  [attr.data-disabled]="disabled"
  [attr.data-error]="error"
>
  @if (label) {
    <label class="input-label" [for]="inputId">
      {{ label }}
      @if (required) {
        <span class="required-indicator" aria-hidden="true">*</span>
      }
    </label>
  }

  <!-- Anchor for popover positioning -->
  <div class="time-picker-anchor">
    @if (isMobile) {
      <div class="input-wrapper">
        <input
          #nativeTimeInput
          class="native-time-picker"
          type="time"
          [ngModel]="inputControl.value"
          [attr.id]="inputId"
          [attr.name]="name"
          [attr.aria-label]="label || placeholder"
          [attr.aria-required]="required"
          [attr.aria-invalid]="error"
          [attr.disabled]="disabled || null"
          (change)="onNativeTimeChange($event)"
          (blur)="onInputBlur()"
          (focus)="onInputFocus()"
        />
        <span
          class="input-icon input-icon-suffix"
          role="button"
          tabindex="0"
          aria-label="Open time picker"
          (click)="onClockIconClick()"
          (keydown.enter)="onClockIconClick()"
          (keydown.space)="onClockIconClick()"
        >
          <umss-icon name="phosphorClock" [size]="iconSize" />
        </span>
      </div>
    } @else {
      <!-- Desktop: Custom masked input with clock popover -->
      <div class="input-wrapper">
        <input
          class="input-field"
          type="text"
          inputmode="numeric"
          mask="Hh:m0"
          [keepCharacterPositions]="true"
          [dropSpecialCharacters]="false"
          [placeholder]="placeholder"
          [formControl]="inputControl"
          [attr.id]="inputId"
          [attr.name]="name"
          [attr.aria-label]="label || placeholder"
          [attr.aria-required]="required"
          [attr.aria-invalid]="error"
          [attr.aria-expanded]="isPopoverOpen"
          [attr.aria-haspopup]="'dialog'"
          (focus)="onInputFocus()"
          (blur)="onInputBlur()"
        />

        <span
          class="input-icon input-icon-suffix"
          role="button"
          tabindex="0"
          aria-label="Open time picker"
          [attr.aria-expanded]="isPopoverOpen"
          (click)="onClockIconClick()"
          (keydown.enter)="onClockIconClick()"
          (keydown.space)="onClockIconClick()"
        >
          <umss-icon name="phosphorClock" [size]="iconSize" />
        </span>
      </div>

      <!-- Clock dropdown: fixed-position panel, managed entirely by time-picker -->
      @if (isPopoverOpen) {
        <div class="time-picker-dropdown" [ngStyle]="dropdownStyle">
          @if (popoverMode() === 'clock') {
            <umss-time-picker-clock
              #clockComponent
              [hours]="clockHours()"
              [minutes]="clockMinutes()"
              [view]="clockView()"
              [hasValue]="hasClockValue"
              [use12Hours]="use12Hours"
              [amPm]="currentAmPm"
              (hourChange)="onHourChange($event)"
              (minuteChange)="onMinuteChange($event)"
              (viewChange)="onClockViewChange($event)"
              (amPmChange)="onAmPmChange($event)"
            />
          } @else {
            <!-- Keyboard input mode -->
            <div class="keyboard-panel">
              <p class="keyboard-title">ENTER TIME</p>
              <div class="keyboard-inputs-row">
                <div class="keyboard-field">
                  <input
                    #hourInput
                    type="text"
                    inputmode="numeric"
                    class="keyboard-input"
                    maxlength="2"
                    placeholder="--"
                    aria-label="Hour"
                    [value]="keyboardHourDisplay()"
                    (input)="onKeyboardHourInput($event)"
                  />
                  <span class="keyboard-label">Hour</span>
                </div>
                <span class="keyboard-separator" aria-hidden="true">:</span>
                <div class="keyboard-field">
                  <input
                    #minuteInput
                    type="text"
                    inputmode="numeric"
                    class="keyboard-input"
                    maxlength="2"
                    placeholder="--"
                    aria-label="Minute"
                    [value]="keyboardMinuteDisplay()"
                    (input)="onKeyboardMinuteInput($event)"
                    (keydown.enter)="onOkClick()"
                  />
                  <span class="keyboard-label">Minute</span>
                </div>
                @if (use12Hours) {
                  <div
                    class="keyboard-ampm-toggle"
                    role="group"
                    aria-label="AM or PM"
                  >
                    <button
                      type="button"
                      class="keyboard-ampm-btn"
                      [class.keyboard-ampm-btn--active]="currentAmPm === 'AM'"
                      (click)="onAmPmChange('AM')"
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      class="keyboard-ampm-btn"
                      [class.keyboard-ampm-btn--active]="currentAmPm === 'PM'"
                      (click)="onAmPmChange('PM')"
                    >
                      PM
                    </button>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Shared footer: always at the same level, same styling in both modes -->
          <div class="popup-footer">
            <button
              type="button"
              class="mode-toggle-btn"
              [attr.aria-label]="
                popoverMode() === 'clock'
                  ? 'Switch to keyboard input'
                  : 'Switch to clock'
              "
              (click)="onToggleMode()"
            >
              @if (popoverMode() === 'clock') {
                <umss-icon name="phosphorPencil" size="16px" />
                <span class="mode-toggle-label">Type</span>
              } @else {
                <umss-icon name="phosphorClock" size="16px" />
                <span class="mode-toggle-label">Clock</span>
              }
            </button>
            @if (requireOkToClose) {
              <div class="popup-actions">
                <button
                  type="button"
                  class="popup-action-btn"
                  (click)="onCancelClick()"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  class="popup-action-btn popup-action-btn--primary"
                  (click)="onOkClick()"
                >
                  OK
                </button>
              </div>
            }
          </div>
        </div>
      }
    }
  </div>

  @if (helperText && !error) {
    <p class="helper-text">{{ helperText }}</p>
  }

  @if (error && errorMessage) {
    <p class="error-message">{{ errorMessage }}</p>
  }
</div>
